import React, { useEffect, useState } from 'react';
import { Link, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { CollegeLogo } from '../../components/CollegeSearchCombobox';
import {
  DeskEmpty,
  Eyebrow,
  PLAN_SHORT,
  SectionHead,
  dateLong,
  spellNumber,
} from './essayShared';

const PLAN_LABELS = {
  ED: 'Early Decision',
  ED_II: 'Early Decision II',
  EA: 'Early Action',
  REA: 'Restrictive Early Action',
  RD: 'Regular Decision',
  ROLLING: 'Rolling',
  UCAS: 'UCAS Main',
  OTHER: 'Other',
};

const STAGE_LABELS = {
  NOT_STARTED: 'Not started',
  ESSAYS: 'Essays',
  SUBMITTED: 'Submitted',
  DECISION: 'Decision',
};

function PlanBadge({ plan }) {
  const solid = plan === 'ED';
  return (
    <span
      title={PLAN_LABELS[plan] || plan}
      style={{
        display: 'inline-block',
        fontSize: 11,
        fontWeight: 600,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        padding: '3px 10px',
        borderRadius: 999,
        background: solid ? 'var(--college-ink)' : 'transparent',
        color: solid ? 'var(--college-panel)' : 'var(--college-ink)',
        border: solid ? 'none' : '1px solid var(--college-ink)',
        whiteSpace: 'nowrap',
      }}
    >
      {PLAN_SHORT[plan] || plan}
    </span>
  );
}

// The student's college list, read by a parent or an advisor: the schools, the
// plans, the dates, and how the essays for each are getting on. Their private
// notes on a school and the AI fit write-up stay behind.
export default function SharedCollegeListPage() {
  const { grantId } = useParams();
  const { grant } = useOutletContext();
  const { api } = useAuth();

  const [applications, setApplications] = useState([]);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(null);

  useEffect(() => {
    let alive = true;
    api
      .get(`/college-access/shared/${grantId}/colleges`)
      .then((res) => {
        if (!alive) return;
        setApplications(res.data.applications || []);
        setProfile(res.data.profile || null);
      })
      .catch((err) => {
        if (!alive) return;
        if (err.response?.status === 403) setDenied(err.response.data.error);
        else toast.error(err.response?.data?.error || 'Failed to load that list');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [api, grantId]);

  if (loading) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <p className="font-mono-editorial" style={{ fontSize: 13, color: 'var(--college-muted)' }}>
          Loading the list…
        </p>
      </div>
    );
  }

  if (denied) {
    return (
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '44px 32px 64px' }}>
        <DeskEmpty
          title="Not shared with you"
          body={`${denied}. ${grant.ownerName} decides what comes through here, and can turn this on at any time.`}
        />
      </div>
    );
  }

  const submitted = applications.filter((a) => a.stage === 'SUBMITTED' || a.stage === 'DECISION').length;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '36px 32px 64px' }}>
      <style>{`
        @media (max-width: 900px) {
          .desk-list-row { grid-template-columns: minmax(0,1fr) !important; row-gap: 8px; }
        }
      `}</style>

      <div
        style={{
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 24,
          borderBottom: '2px solid var(--college-ink)',
          paddingBottom: 14,
          flexWrap: 'wrap',
        }}
      >
        <div>
          <Eyebrow>
            {applications.length === 0
              ? `${grant.ownerName}'s list`
              : `${spellNumber(applications.length, { capital: true })} school${
                  applications.length === 1 ? '' : 's'
                } · ${spellNumber(submitted)} submitted`}
          </Eyebrow>
          <h1
            className="font-serif-editorial"
            style={{ fontSize: 44, fontWeight: 400, lineHeight: 1, margin: 0, color: 'var(--college-ink)' }}
          >
            The List
          </h1>
        </div>
        {profile && (
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-end' }}>
            {[
              { label: 'SAT', value: profile.satComposite },
              { label: 'IB', value: profile.ibPredicted },
              { label: 'GPA', value: profile.gpa },
            ].map(({ label, value }) => (
              <div key={label}>
                <p
                  style={{
                    margin: '0 0 2px',
                    font: "600 10px/1 'Instrument Sans', sans-serif",
                    letterSpacing: '0.14em',
                    textTransform: 'uppercase',
                    color: 'var(--college-muted-2)',
                  }}
                >
                  {label}
                </p>
                <p className="font-mono-editorial" style={{ margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--college-ink)' }}>
                  {value != null && value !== '' ? value : '—'}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      {applications.length === 0 ? (
        <div style={{ marginTop: 32 }}>
          <DeskEmpty title="No schools on the list yet" body={`${grant.ownerName} hasn't added any schools to their list.`} />
        </div>
      ) : (
        <div style={{ marginTop: 26 }}>
          <SectionHead
            title="Where they're applying"
            size={24}
            right={
              <span style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                {[
                  { label: 'Reach', color: 'var(--college-reach)' },
                  { label: 'Target', color: 'var(--college-target)' },
                  { label: 'Likely', color: 'var(--college-likely)' },
                ].map(({ label, color }) => (
                  <span
                    key={label}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      color: 'var(--college-muted)',
                      letterSpacing: '0.04em',
                      textTransform: 'uppercase',
                    }}
                  >
                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                    {label}
                  </span>
                ))}
              </span>
            }
          />

          <div style={{ background: 'var(--college-panel)', border: '1px solid var(--college-border-soft)', borderTop: 'none' }}>
            {applications.map((app, i) => (
              <div
                key={app.id}
                className="desk-list-row"
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0,1fr) 96px 150px 130px 92px',
                  alignItems: 'center',
                  gap: 16,
                  padding: '15px 18px',
                  borderBottom: i === applications.length - 1 ? 'none' : '1px solid var(--college-border-soft)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                  <CollegeLogo schoolUrl={app.schoolUrl} name={app.collegeName} size={26} />
                  <div style={{ minWidth: 0 }}>
                    <span className="font-serif-editorial" style={{ fontSize: 18, color: 'var(--college-ink)' }}>
                      {app.collegeName}
                    </span>
                    <p style={{ margin: '3px 0 0', fontSize: 12, color: 'var(--college-muted)' }}>
                      {[app.city && `${app.city}${app.region ? `, ${app.region}` : ''}`, STAGE_LABELS[app.stage]]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                  </div>
                </div>

                <PlanBadge plan={app.plan} />

                <span className="font-mono-editorial" style={{ fontSize: 12.5, color: 'var(--college-ink-soft)' }}>
                  {dateLong(app.deadline)} · {app.days}d
                </span>

                {grant.seeProgress ? (
                  <Link
                    to={`/shared/${grantId}/essays`}
                    style={{ fontSize: 12, color: 'var(--college-ink-soft)', textDecoration: 'none' }}
                  >
                    {app.essayCount === 0
                      ? 'No essays yet'
                      : `${app.essaysSubmitted}/${app.essayCount} essays done`}
                    {app.essaysNeedingSomeone > 0 && (
                      <span style={{ color: 'var(--college-red)' }}> · {app.essaysNeedingSomeone} waiting</span>
                    )}
                  </Link>
                ) : (
                  <span style={{ fontSize: 12, color: 'var(--college-muted-2)' }}>—</span>
                )}

                {app.fit ? (
                  <span
                    style={{
                      justifySelf: 'end',
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '0.06em',
                      textTransform: 'uppercase',
                      padding: '4px 12px',
                      borderRadius: 999,
                      background: app.fitBg,
                      color: app.fitColor,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {app.fit}
                  </span>
                ) : (
                  <span style={{ justifySelf: 'end', fontSize: 11, color: 'var(--college-muted-2)' }}>—</span>
                )}
              </div>
            ))}
          </div>

          <p style={{ marginTop: 16, fontSize: 12, color: 'var(--college-muted)' }}>
            Fit tiers compare {grant.ownerName}'s numbers against each school. Their own notes on a school aren't part
            of what's shared.
          </p>
        </div>
      )}
    </div>
  );
}
